"use client";

import React, { useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import type {
  IPlanPricing,
  IPlanFundingMode,
  IPlanAccountType,
  IPricingTypeOther,
} from "@/sanity/pages/PageAssetsTemplate";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import checkbg from "@/public/pricing-table/checkbg.jpg";

// PricingCard Component
interface PricingCardProps {
  account: IPlanAccountType;
  accountIndex: number;
  pageType: "crypto" | "futures";
  fundingModeName: string;
  InfoRow: React.FC<{
    label: string;
    value?: string | number;
    showBorder?: boolean;
  }>;
}

function PricingCard({
  account,
  accountIndex,
  pageType,
  fundingModeName,
  InfoRow,
}: PricingCardProps) {
  const typeDetails = account.types?.[0]; // Get first type detail

  return (
    <div className="flex flex-col gap-[.75rem]">
      {/* Details */}
      {typeDetails && (
        <div className="flex flex-col rounded-[1.25rem] overflow-hidden">
          {/* Header */}

          <div className="flex flex-col gap-[1.25rem] bg-[#E6EDE9]/40 pt-[2rem] pb-[2.25rem] px-[3rem]">
            <div className="flex flex-col items-center gap-[.5rem]">
              <h3 className="text-[4rem] font-[500] font-geist text-[#103037] mb-0">
                {account.name}
              </h3>
              {account.level ? (
                <p className="text-[2rem] text-[#103037]/60 mb-0">
                  {account.level}
                </p>
              ) : (
                <p className="text-[2rem] text-[#103037]/60 mb-0 opacity-0">
                  level
                </p>
              )}
              <hr className="w-full border-t border-[#103037]/10 my-[2rem]" />
            </div>
            {/* Crypto Challenge Fields */}
            {pageType === "crypto" &&
              (fundingModeName === "Challenge" ||
                fundingModeName.toLowerCase().includes("challenge")) && (
                <>
                  <InfoRow
                    label="Profit Target"
                    value={typeDetails.profitTarget}
                  />
                  <InfoRow
                    label="Maximum Drawdown"
                    value={typeDetails.maximumDrawdown}
                  />
                  <InfoRow
                    label="Drawdown Mode"
                    value={typeDetails.drawdownMode}
                  />
                  <InfoRow
                    label="Position Loss (flat)"
                    value={typeDetails.positionLossFlat}
                  />
                  <InfoRow label="Contracts" value={typeDetails.contracts} />
                  <InfoRow
                    label="Challenge Duration"
                    value={typeDetails.challengeDuration}
                  />
                  <InfoRow
                    label="Activation Fee"
                    value={typeDetails.activationFee}
                    showBorder={false}
                  />
                </>
              )}

            {/* Crypto Instant Funding Fields */}
            {pageType === "crypto" &&
              (fundingModeName === "Instant Funding" ||
                fundingModeName.toLowerCase().includes("instant")) && (
                <>
                  {/*<InfoRow
                    label="Account Balance"
                    value={typeDetails.accountBalance}
                  />*/}
                  <InfoRow
                    label="Max Drawdown"
                    value={typeDetails.maxDrawdown}
                  />
                  <InfoRow
                    label="Drawdown Mode"
                    value={typeDetails.drawdownMode}
                  />
                  <InfoRow
                    label="Max Position Loss (flat)"
                    value={typeDetails.positionLossFlat}
                  />
                  <InfoRow
                    label="Consistency"
                    value={typeDetails.consistency}
                  />
                  <InfoRow label="Contracts" value={typeDetails.contracts} />
                  <InfoRow
                    label="Minimum Trading Days"
                    value={typeDetails.minimumTradingDays}
                  />
                  <InfoRow
                    label="Duration"
                    value={typeDetails.challengeDuration}
                  />
                  {/*<InfoRow
                    label="Price"
                    value={typeDetails.price}
                    showBorder={false}
                  />*/}
                </>
              )}

            {/* Futures Challenge Fields */}
            {pageType === "futures" &&
              (fundingModeName === "Challenge" ||
                fundingModeName.toLowerCase().includes("challenge")) && (
                <>
                  <InfoRow
                    label="Profit Target"
                    value={typeDetails.profitTarget}
                  />
                  <InfoRow
                    label="Max Drawdown"
                    value={typeDetails.maxDrawdown}
                  />
                  <InfoRow
                    label="Drawdown Mode"
                    value={typeDetails.drawdownMode}
                  />
                  <InfoRow
                    label="Max Position Loss"
                    value={typeDetails.positionLossFlat}
                  />
                  <InfoRow label="Contracts" value={typeDetails.contracts} />
                  <InfoRow
                    label="Challenge Duration"
                    value={typeDetails.challengeDuration}
                  />
                  <InfoRow
                    label="Activation Fee"
                    value={typeDetails.activationFee}
                  />
                  <InfoRow
                    label="Price"
                    value={typeDetails.price}
                    showBorder={false}
                  />
                </>
              )}

            {/* Futures Instant Funding Fields */}
            {pageType === "futures" &&
              (fundingModeName === "Instant Funding" ||
                fundingModeName.toLowerCase().includes("instant")) && (
                <>
                  <InfoRow
                    label="Max Drawdown"
                    value={typeDetails.maxDrawdown}
                  />
                  <InfoRow
                    label="Drawdown Mode"
                    value={typeDetails.drawdownMode}
                  />
                  <InfoRow
                    label="Max Position Loss (flat)"
                    value={typeDetails.positionLossFlat}
                  />
                  <InfoRow
                    label="Consistency"
                    value={typeDetails.consistency}
                  />
                  <InfoRow label="Contracts" value={typeDetails.contracts} />
                  <InfoRow
                    label="Minimum Trading Days"
                    value={typeDetails.minimumTradingDays}
                  />
                  <InfoRow
                    label="Duration"
                    value={typeDetails.challengeDuration}
                  />
                  <InfoRow
                    label="Price"
                    value={typeDetails.price}
                    showBorder={false}
                  />
                </>
              )}

            <div className="flex flex-col lg:gap-[1.5rem] gap-[1rem] pt-[1rem] lg:pt-[1.75rem]">
              {/* Price Display */}
              {account.price && (
                <div className="flex items-baseline justify-center gap-[.75rem]">
                  <span className="text-[4.75rem] font-[500] font-geist text-[#103037]">
                    {account.hasDiscount && account.discountedPrice
                      ? account.discountedPrice
                      : account.price}
                  </span>
                  {account.hasDiscount && account.discountedPrice && (
                    <span className="text-[2.25rem] font-[500] font-geist text-[#103037]/50 line-through">
                      {account.price}
                    </span>
                  )}
                  {account.feeText && (
                    <span className="text-[1.75rem] font-[500] font-geist text-[#103037]/60">
                      {account.feeText}
                    </span>
                  )}
                </div>
              )}

              {/* CTA Button */}
              {account.cta && (
                <CTAButton data={account.cta} className="w-full" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PricingTableAssets({
  planPricing = [],
  pageType = "crypto",
  lang,
  stepOneTitle = "Funding Mode",
}: {
  planPricing?: IPlanPricing[];
  pageType?: "crypto" | "futures";
  lang: "en" | "de" | "br";
  stepOneTitle?: string;
}) {
  const [selectedPricingIndex, setSelectedPricingIndex] = useState<number>(0);
  const [selectedFundingModeIndex, setSelectedFundingModeIndex] =
    useState<number>(0);
  const [selectedAccountTypeIndex, setSelectedAccountTypeIndex] =
    useState<number>(0);

  const selectedPricing = planPricing[selectedPricingIndex];
  const selectedFundingMode =
    selectedPricing?.fundingMode?.[selectedFundingModeIndex];

  const InfoRow = ({
    label,
    value,
    showBorder = true,
  }: {
    label: string;
    value?: string | number;
    showBorder?: boolean;
  }) => (
    <div
      className={cn(
        "flex justify-between items-center pb-[1.88rem]",
        showBorder && "border-b border-#CECECE/70 border-dashed lg:border-none",
      )}
    >
      <span className="text-[2.25rem] lg:text-[1.75rem] font-geist text-[#103037]/60 lg:hidden">
        {label}
      </span>
      <span className="text-[2.25rem] lg:text-[2.25rem] font-geist text-[#103037]">
        {value ?? "/"}
      </span>
    </div>
  );

  const InfoRowLabel = ({
    label,
    showBorder = true,
  }: {
    label: string;
    showBorder?: boolean;
  }) => (
    <div
      className={cn("relative flex justify-between items-center pb-[1.88rem]")}
    >
      <span className="text-[2.25rem] font-geist text-[#103037]/50 uppercase">
        {label}
      </span>
      {showBorder && (
        <div className="absolute left-0 right-0 bottom-0 border-b border-[#CECECE]/70 border-dashed w-[194rem]" />
      )}
    </div>
  );

  // Get all account types for the selected funding mode
  const accountTypes = selectedFundingMode?.accountType || [];

  // Debug: Show message if no data
  if (!planPricing || planPricing.length === 0) {
    return (
      <div className="flex flex-col w-full gap-[2rem]">
        <div className="flex relative flex-col bg-white/90 p-[2.5rem] gap-[3rem] lg:gap-[6rem] lg:py-[4rem] lg:px-[6rem] rounded-[2rem] lg:rounded-[3rem]">
          <div className="text-center py-[4rem]">
            <h3 className="text-[2.5rem] text-[#103037] mb-[1rem]">
              No Pricing Data Available
            </h3>
            <p className="text-[1.75rem] text-[#103037]/60">
              Please add pricing data in Sanity CMS.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-[min(200rem,100%)] gap-[2rem]">
      {/* Funding Mode Selection */}
      <div className="flex relative flex-col bg-white/90 gap-[3rem] lg:rounded-[3rem]">
        <div className="flex flex-col gap-[1.5rem]">
          <div className="flex items-center gap-[2rem]">
            <h4 className="text-[2rem] font-geist font-[500] text-[#103037]/60 mb-0 whitespace-nowrap shrink-0 uppercase">
              {stepOneTitle}
            </h4>
            <div className="flex-1 h-[1px] bg-[#CECECE]/40"></div>
          </div>
          {!planPricing || planPricing.length === 0 ? (
            <div className="text-center py-[2rem] text-[1.75rem] text-[#103037]/60">
              No pricing options available.
            </div>
          ) : (
            <div className="lg:pl-[41.5rem] flex flex-col lg:flex-row gap-[1.5rem] flex-wrap">
              {planPricing.map((pricing, index) => (
                <div className="relative lg:w-[78.5rem]" key={index}>
                  <button
                    onClick={() => {
                      setSelectedPricingIndex(index);
                      setSelectedAccountTypeIndex(0);
                    }}
                    className={cn(
                      "bg-[#F4F7F5] border relative flex flex-col text-start p-[2rem] lg:pl-[3rem] lg:py-[2.5rem] rounded-[1.375rem] transition-all w-full lg:w-fit",
                      selectedPricingIndex === index
                        ? "border-[#103037]/30"
                        : "border-transparent",
                    )}
                  >
                    <div className="flex items-start gap-[2rem]">
                      {selectedPricingIndex === index ? (
                        <div className="mt-[1rem] flex items-center justify-center w-[3rem] h-[3rem] lg:w-[3rem] lg:h-[3rem] rounded-full bg-[#103037] flex-shrink-0">
                          <Check className="w-[2rem] h-[2rem] lg:w-[2rem] lg:h-[2rem] text-white" />
                        </div>
                      ) : (
                        <div className="mt-[1rem] flex items-center justify-center w-[3rem] h-[3rem] lg:w-[3rem] lg:h-[3rem] rounded-full border border-[#AFBBBC] flex-shrink-0" />
                      )}
                      <div className="relative">
                        <span
                          className={cn(
                            "font-[500] text-[3rem] transition-all text-black",
                          )}
                        >
                          {pricing.fundingMode?.[0]?.name || "Unnamed"}
                        </span>
                        {pricing.fundingMode?.[0]?.description && (
                          <p
                            className={cn(
                              "text-[2.25rem] lg:text-[2rem] leading-[150%] mb-0 text-black/80",
                            )}
                          >
                            {pricing.fundingMode[0].description}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-[2rem]">
          <h4 className="text-[2rem] font-geist font-[500] text-[#103037]/60 mb-0 whitespace-nowrap shrink-0 uppercase">
            Account Type
          </h4>
          <div className="flex-1 h-[1px] bg-[#CECECE]/40"></div>
        </div>
      </div>

      {/*desktop*/}
      <div className="hidden lg:block relative bg-white/90 rounded-[2rem] lg:rounded-[3rem]">
        <div className="flex gap-[2rem]">
          <div className="w-[38.5rem] flex flex-col gap-[.75rem] shrink-0">
            <div className="flex flex-col rounded-[1.25rem] overflow-visible">
              <div className="flex flex-col gap-[1.25rem] pt-[2rem] pb-[2.25rem] px-[3rem] overflow-visible">
                <div className="flex flex-col items-center gap-[.5rem]">
                  <h3 className="text-[4rem] font-[500] font-geist text-[#103037] mb-0 opacity-0">
                    Placeholder
                  </h3>
                  <p className="text-[2rem] text-[#103037]/60 mb-0 opacity-0">
                    Level
                  </p>
                  <hr className="w-full border-t border-[#103037]/10 my-[2rem] opacity-0" />
                </div>

                {/* Crypto Challenge Field Labels */}
                {pageType === "crypto" &&
                  (selectedFundingMode?.name === "Challenge" ||
                    selectedFundingMode?.name
                      .toLowerCase()
                      .includes("challenge")) && (
                    <>
                      <InfoRowLabel label="Profit Target" />
                      <InfoRowLabel label="Max Drawdown" />
                      <InfoRowLabel label="Drawdown Mode" />
                      <InfoRowLabel label="Max Position Loss" />
                      <InfoRowLabel label="Contracts" />
                      <InfoRowLabel label="Challenge Duration" />
                      <InfoRowLabel label="Activation Fee" />
                      <InfoRowLabel label="Price" showBorder={false} />
                    </>
                  )}

                {/* Crypto Instant Funding Field Labels */}
                {pageType === "crypto" &&
                  (selectedFundingMode?.name === "Instant Funding" ||
                    selectedFundingMode?.name
                      .toLowerCase()
                      .includes("instant")) && (
                    <>
                      <InfoRowLabel label="Max Drawdown" />
                      <InfoRowLabel label="Drawdown Mode" />
                      <InfoRowLabel label="Max Position Loss (flat)" />
                      <InfoRowLabel label="Consistency" />
                      <InfoRowLabel label="Contracts" />
                      <InfoRowLabel label="Minimum Trading Days" />
                      <InfoRowLabel label="Duration" />
                      <InfoRowLabel label="Price" showBorder={false} />
                    </>
                  )}

                {/* Futures Challenge Field Labels */}
                {pageType === "futures" &&
                  (selectedFundingMode?.name === "Challenge" ||
                    selectedFundingMode?.name
                      .toLowerCase()
                      .includes("challenge")) && (
                    <>
                      <InfoRowLabel label="Profit Target" />
                      <InfoRowLabel label="Max Drawdown" />
                      <InfoRowLabel label="Drawdown Mode" />
                      <InfoRowLabel label="Max Position Loss" />
                      <InfoRowLabel label="Contracts" />
                      <InfoRowLabel label="Challenge Duration" />
                      <InfoRowLabel label="Activation Fee" />
                      <InfoRowLabel label="Price" showBorder={false} />
                    </>
                  )}

                {/* Futures Instant Funding Field Labels */}
                {pageType === "futures" &&
                  (selectedFundingMode?.name === "Instant Funding" ||
                    selectedFundingMode?.name
                      .toLowerCase()
                      .includes("instant")) && (
                    <>
                      <InfoRowLabel label="Max Drawdown" />
                      <InfoRowLabel label="Drawdown Mode" />
                      <InfoRowLabel label="Max Position Loss (flat)" />
                      <InfoRowLabel label="Consistency" />
                      <InfoRowLabel label="Contracts" />
                      <InfoRowLabel label="Minimum Trading Days" />
                      <InfoRowLabel label="Duration" />
                      <InfoRowLabel label="Price" showBorder={false} />
                    </>
                  )}
              </div>
            </div>
          </div>
          {accountTypes.map((account, accountIndex) => (
            <div key={accountIndex} className="shrink-0 w-fit flex-1">
              <PricingCard
                account={account}
                accountIndex={accountIndex}
                pageType={pageType}
                fundingModeName={selectedFundingMode?.name || ""}
                InfoRow={InfoRow}
              />
            </div>
          ))}
        </div>
      </div>

      {/*mobile*/}
      <div className="relative bg-white/90 rounded-[2rem] lg:hidden">
        {/* Account Type Buttons */}
        <div className="flex gap-[1rem]">
          {accountTypes.map((account, accountIndex) => (
            <button
              key={accountIndex}
              onClick={() => setSelectedAccountTypeIndex(accountIndex)}
              className={cn(
                "flex-1 py-[1.5rem] px-[2rem] rounded-[2rem] text-[3rem] font-[500] transition-all",
                selectedAccountTypeIndex === accountIndex
                  ? "bg-[#103037] text-white"
                  : "bg-[#F3F7F5] text-[#103037]",
              )}
            >
              {account.name}
            </button>
          ))}
        </div>

        {/* Selected Pricing Card */}
        <div className="pt-[2rem]">
          {accountTypes[selectedAccountTypeIndex] && (
            <PricingCard
              account={accountTypes[selectedAccountTypeIndex]}
              accountIndex={selectedAccountTypeIndex}
              pageType={pageType}
              fundingModeName={selectedFundingMode?.name || ""}
              InfoRow={InfoRow}
            />
          )}
        </div>
      </div>
    </div>
  );
}
